/**
 * Import history (TZ-04 §D): every migration job this workspace has run —
 * single HTML imports and bulk-zip jobs alike — newest first, paginated via
 * the cursor from GET /v1/migrations. Lets users revisit a past import,
 * see how many files converted/failed, why a job failed, and jump straight
 * to a successfully imported template.
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { PortalShell } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import { listMigrations, type MigrationJob } from "../lib/migrations";

const PAGE_LIMIT = 25;

type StatusFilter = "all" | MigrationJob["status"];

const STATUS_FILTERS: StatusFilter[] = [
  "all",
  "completed",
  "failed",
  "in_progress",
  "queued",
  "cancelled",
];

const STATUS_STYLE: Record<MigrationJob["status"], string> = {
  completed: "bg-success-50 text-success-700",
  failed: "bg-error-50 text-error-700",
  in_progress: "bg-warning-50 text-warning-700",
  queued: "bg-warning-50 text-warning-700",
  cancelled: "bg-neutral-100 text-neutral-600",
};

function formatTime(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "";
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

export function ImportsPage(): JSX.Element {
  const [status, setStatus] = useState<StatusFilter>("all");

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["migrations", "list", status],
      queryFn: ({ pageParam }) =>
        listMigrations({
          limit: PAGE_LIMIT,
          cursor: pageParam,
          status: status === "all" ? undefined : status,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last) =>
        last.pagination.has_more ? (last.pagination.next_cursor ?? undefined) : undefined,
    });

  const jobs = data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <PortalShell>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Imports</h1>
          <p className="text-sm text-neutral-600">
            Every HTML and bulk-zip migration this workspace has run.
          </p>
        </div>
        <label className="text-sm">
          <span className="mr-2 text-neutral-600">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All" : s.replace("_", " ")}
              </option>
            ))}
          </select>
        </label>
      </header>

      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
        {isLoading ? (
          <p className="px-6 py-12 text-sm text-neutral-500">Loading…</p>
        ) : error instanceof ApiError ? (
          <p
            role="alert"
            className="m-6 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error.problem.detail || error.problem.title}
          </p>
        ) : jobs.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-neutral-500">
            No imports yet — bring an email in from the Templates page.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {jobs.map((job) => (
              <ImportRow key={job.id} job={job} />
            ))}
          </ul>
        )}
      </section>

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </PortalShell>
  );
}

function ImportRow({ job }: { job: MigrationJob }): JSX.Element {
  const succeeded = job.succeeded_files ?? 0;
  const failed = job.failed_files ?? 0;
  const total = job.total_files ?? 0;
  // A single-HTML import is one file with a template_id; a bulk job is many.
  const isSingle = total <= 1 && job.template_id != null && job.template_id !== "";

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLE[job.status]}`}
          >
            {job.status.replace("_", " ")}
          </span>
          <span className="text-sm font-medium capitalize text-neutral-900">
            {job.source_platform}
          </span>
        </p>
        <p className="mt-1 text-xs text-neutral-600">
          {isSingle
            ? "1 template imported"
            : `${succeeded} converted, ${failed} failed${total > 0 ? ` of ${total}` : ""}`}
          {(job.warnings_count ?? 0) > 0 && (
            <span className="text-neutral-500"> · {job.warnings_count} warnings</span>
          )}
        </p>
        {job.status === "failed" && job.error?.detail != null && (
          <p className="mt-1 text-xs text-error-700">{job.error.detail}</p>
        )}
      </div>
      <div className="flex items-center gap-4">
        <span className="whitespace-nowrap text-xs text-neutral-500">
          {formatTime(job.created_at)}
        </span>
        {job.template_id != null && job.template_id !== "" && (
          <Link
            to={`/templates/${job.template_id}/edit`}
            className="whitespace-nowrap text-sm font-medium text-primary-600 hover:underline"
          >
            Open template
          </Link>
        )}
      </div>
    </li>
  );
}
